import { Dialog, Menu, Popover, Transition } from '@headlessui/react';
import React, { Fragment, useCallback, useEffect, useTransition } from 'react';
import EventLabel from '../../components/EventLabel/EventLabel';
import PersonListItem from './PersonListItem';
import BookingDetails from './BookingDetails';
import { useStore } from '../../store/Store';
import cx from 'classnames';
import Accordion from '../../components/Accordion';
import { basedAxios } from '../../store/Store';
import _ from 'lodash';
import { TablePagination } from '@material-ui/core';
import { encodeToBase64 } from '../../utils/encode.utils';

interface IBookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderList: any[];
  availableTimeslots: any[];
  myMeetings: any[];
  myOrder: any;
  style: any;
  fixedTimeslot: any;
  settings: any;
}

const placeHolderTexts: any = {
  swe: {
    SearchForPerson: 'Sök efter person eller företag...',
  },
  eng: {
    SearchForPerson: 'Search for person or company...',
  },
};

export default function BookingDialog({
  isOpen,
  onClose,
  availableTimeslots,
  myMeetings,
  myOrder,
  style,
  settings,
  fixedTimeslot = null,
}: IBookingDialogProps) {
  const [bookerId, setBookerId] = React.useState<number | null>(null);
  const [searchKey, setSearchKey] = React.useState('');
  const [filters, setFilters] = React.useState<any[]>([]);
  const [orderList, setOrderList] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);

  const { environment, questions } = useStore();
  const { lang } = environment;
  const { selection, enableQuestionAnswerSearch, sortByCompany = false, questionAnswers = [] } = settings;

  useEffect(() => {
    (async () => {
      await handleSearch('');
    })();
  }, []);

  const handleSearch = async (value: string, pageNo?: number | null) => {
    try {
      setIsLoading(true);

      const code = encodeToBase64(`${myOrder?.orderId}:${myOrder?.badgeNo}`);

      const response = await basedAxios.get('./matchMaking/search/' + myOrder?.eventId, {
        params: {
          searchKey: value ?? '',
          page: pageNo ?? 1,
          code,
        },
      });
      setOrderList(response?.data?.data);
      setPage(response?.data?.page);
      setTotalPages(response?.data?.totalPages);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedHandleSearch = useCallback(
    _.debounce((value: string) => handleSearch(value), 500),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKey(value);
    // Use the debounced function
    debouncedHandleSearch(value);
  };

  const filterableQuestions = questions?.filter(({ questionId }: { questionId: number }) => selection?.includes(questionId));
  const filteredOrderList = React.useMemo(() => {
    return orderList
      ?.filter(order => {
        const filterMatch = enableQuestionAnswerSearch
          ? filters.some(({ questionId: qId, questionOptionId: qoId }) =>
              order.questions.some(({ questionId, questionOptionId }: any) => qId === questionId && qoId === questionOptionId)
            )
          : true;
        return filterMatch;
      })
      ?.filter(order => order.orderId !== myOrder?.orderId);
    // ?.sort((a, b) => (sortByCompany ? a.companyName.localeCompare(b.companyName) : a.firstName.localeCompare(b.firstName)));
  }, [orderList, searchKey, enableQuestionAnswerSearch, sortByCompany, filters]);

  const handleFilterChange = React.useCallback(
    (questionId: number, questionOptionId: number) => {
      if (
        filters?.some(
          ({ questionId: qId, questionOptionId: oId }: { questionId: number; questionOptionId: number }) => qId === questionId && oId === questionOptionId
        )
      ) {
        setFilters(
          filters?.filter(
            ({ questionId: qId, questionOptionId: oId }: { questionId: number; questionOptionId: number }) => !(qId === questionId && oId === questionOptionId)
          )
        );
      } else {
        setFilters([...filters, { questionId, questionOptionId }]);
      }
    },
    [filters]
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto" style={{ fontFamily: style?.['font-family'] }}>
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform rounded-2xl bg-white  text-left align-middle shadow-xl transition-all">
                {!bookerId ? (
                  <div className="p-6">
                    <span className="flex items-center text-lg gap-x-2  w-full relative">
                      <i className="ri-user-search-line"></i>
                      <span>
                        <EventLabel name="BookingMeeting" groupName="CMS.MatchMaking" />
                      </span>
                      <i className="ri-close-line absolute right-1 -top-1 cursor-pointer" onClick={onClose} />
                    </span>
                    <div className="flex items-center">
                      <input
                        className={cx('border border-divider my-2 h-10 w-full rounded-md', enableQuestionAnswerSearch ? 'rounded-br-none rounded-tr-none' : '')}
                        placeholder={placeHolderTexts?.[lang]?.SearchForPerson}
                        value={searchKey}
                        onChange={e => handleInputChange(e)}
                      />
                      <Popover as="div" className="relative inline-block text-left">
                        <div>
                          {enableQuestionAnswerSearch && (
                            <Popover.Button className="bg-white border-l-0 rounded-tl-none rounded-bl-none border border-divider relative h-10   py-2 flex-center rounded-md">
                              <i className="ri-filter-line" />
                              {filters.length > 0 && (
                                <div className="absolute  -top-1 -right-1 bg-primary rounded-full text-[10px] flex justify-center items-center   h-4 min-w-4 px-[5px] text-white ">
                                  <span className="flex " style={{ lineHeight: 3 }}>
                                    {filters.length}
                                  </span>
                                </div>
                              )}
                            </Popover.Button>
                          )}
                        </div>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Popover.Panel className="absolute z-20 -left-[245px] top-10 mt-2 w-auto min-w-72 shadow-sm origin-top-right border px-4 py-2 border-divider rounded-md bg-white  focus:outline-none">
                            {filterableQuestions?.map(
                              (
                                { questionId, questionOption, questionLanguage }: { questionId: number; questionOption: any; questionLanguage: any },
                                index: number
                              ) => (
                                <div key={questionId} className={cx(index !== filterableQuestions.length - 1 ? 'border-b border-b-divider' : '')}>
                                  <Accordion
                                    index={0}
                                    defaultOpen={questionOption.length < 5}
                                    header={
                                      <div className="">
                                        <div className=" py-0 text-sm flex items-center gap-x-2  font-semibold">
                                          {filters?.filter(({ questionId: qId }: { questionId: number }) => qId === questionId)?.length > 0 && (
                                            <div className=" absolute  right-14 bg-primary font-normal rounded-full text-[10px] flex justify-center items-center  h-4 min-w-4 px-[5px] text-white ">
                                              <span className="flex " style={{ lineHeight: 3 }}>
                                                {filters?.filter(({ questionId: qId }: { questionId: number }) => qId === questionId)?.length}
                                              </span>
                                            </div>
                                          )}

                                          {questionLanguage?.find(({ languageCode }: { languageCode: string }) => languageCode === lang)?.label}
                                        </div>
                                      </div>
                                    }
                                  >
                                    <div className={cx('pb-4')}>
                                      {questionOption.map(
                                        ({ questionOptionId, questionOptionLanguage }: { questionOptionId: number; questionOptionLanguage: any }) => (
                                          <div key={questionOptionId}>
                                            <div
                                              className={`flex justify-between px-4 py-2 text-sm cursor-pointer hover:bg-neutral-100`}
                                              onMouseDown={e => handleFilterChange(questionId, questionOptionId)}
                                            >
                                              <div className="flex gap-x-2">
                                                <input
                                                  type="checkbox"
                                                  className="border border-divider rounded-md accent-primary"
                                                  checked={
                                                    !!filters?.some(
                                                      ({ questionId: qId, questionOptionId: oId }: { questionId: number; questionOptionId: number }) =>
                                                        qId === questionId && oId === questionOptionId
                                                    )
                                                  }
                                                />
                                                <span>
                                                  {questionOptionLanguage.find(({ languageCode }: { languageCode: string }) => languageCode === lang)?.label}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </Accordion>
                                </div>
                              )
                            )}
                          </Popover.Panel>
                        </Transition>
                      </Popover>
                    </div>

                    <div className="h-[75vh]">
                      <div className="max-h-[70vh] min-h-[70vh] overflow-y-auto divide-y border border-divider rounded-md divide-divider">
                        {!isLoading ? (
                          filteredOrderList?.map(order => (
                            <Fragment key={order.orderId}>
                              <PersonListItem onSelect={() => setBookerId(order.orderId)} questionAnswers={questionAnswers} lang={lang} {...order} />
                            </Fragment>
                          ))
                        ) : (
                          <div className="p-1 flex flex-col gap-1">
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                            <div className="animate-pulse h-12 w-full bg-slate-200" />
                          </div>
                        )}
                      </div>
                      {totalPages > 1 && (
                        <TablePagination
                          onPageChange={(e, page) => handleSearch(searchKey, page + 1)}
                          page={page - 1}
                          rowsPerPageOptions={[]}
                          component={'div'}
                          rowsPerPage={searchKey === '' ? 50 : filteredOrderList?.length} // You can keep it as 1 or any value depending on how many items per page you want.
                          count={searchKey === '' ? totalPages * 50 : filteredOrderList?.length} // This is the total number of items you have.
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <BookingDetails
                      myMeetings={myMeetings}
                      availableTimeSlots={availableTimeslots}
                      fixedTimeslot={fixedTimeslot}
                      myOrder={myOrder}
                      settings={settings}
                      goBack={() => setBookerId(null)}
                      order={orderList?.find(({ orderId }) => orderId === bookerId)}
                    />
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
